import { useState } from "react";
import type { Notification } from "../../../lib/client/types.gen";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
	Mail,
	Phone,
	ChevronLeft,
	ChevronRight,
	Search,
	Loader,
	Eye,
	X,
	RefreshCw,
	AlertCircle,
} from "lucide-react";
import {
	getAllNotificationsOptions,
	getFailedNotificationsOptions,
	retryNotificationMutation,
	retryAllFailedNotificationsMutation,
} from "@/lib/client/@tanstack/react-query.gen";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/configuration/notifications")({
	component: NotificationsPage,
});

export default function NotificationsPage() {
	const [searchTerm, setSearchTerm] = useState("");
	const [currentPage, setCurrentPage] = useState(0);
	const [pageSize, setPageSize] = useState(50);
	const [sortBy] = useState("sentAt");
	const [sortDirection] = useState("DESC");
	const [selectedNotification, setSelectedNotification] =
		useState<Notification | null>(null);
	const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
	const [filterStatus, setFilterStatus] = useState<string>("ALL");
	const queryClient = useQueryClient();

	const {
		data: notifications,
		isLoading,
		error,
	} = useQuery(
		getAllNotificationsOptions({
			query: {
				page: currentPage,
				size: pageSize,
				sortBy,
				sortDirection,
			},
		}),
	);

	const {
		data: failedNotifications,
		isLoading: isLoadingFailed,
	} = useQuery(getFailedNotificationsOptions());

	const retryMutation = useMutation({
		...retryNotificationMutation(),
		onSuccess: (data) => {
			if (data.status === "SENT") {
				alert("✅ La notification a été renvoyée avec succès");
			} else {
				alert(`❌ Échec de l'envoi: ${data.errorMessage}`);
			}
			queryClient.invalidateQueries({ queryKey: ["getAllNotifications"] });
			queryClient.invalidateQueries({ queryKey: ["getFailedNotifications"] });
		},
		onError: (error) => {
			alert("❌ Une erreur est survenue lors de la relance");
		},
	});

	const retryAllMutation = useMutation({
		...retryAllFailedNotificationsMutation(),
		onSuccess: (data) => {
			const successCount = data.filter((n) => n.status === "SENT").length;
			const failedCount = data.filter((n) => n.status === "FAILED").length;
			alert(`✅ Relance terminée: ${successCount} envoyées, ${failedCount} échouées`);
			queryClient.invalidateQueries({ queryKey: ["getAllNotifications"] });
			queryClient.invalidateQueries({ queryKey: ["getFailedNotifications"] });
		},
		onError: (error) => {
			alert("❌ Une erreur est survenue lors de la relance globale");
		},
	});

	function getStatusColor(status?: string) {
		switch (status) {
			case "SENT":
				return "bg-green-100 text-green-800";
			case "FAILED":
				return "bg-red-100 text-red-800";
			case "PENDING":
				return "bg-yellow-100 text-yellow-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	}

	function getTypeIcon(type?: string) {
		switch (type) {
			case "EMAIL":
				return <Mail className="h-4 w-4" />;
			case "SMS":
				return <Phone className="h-4 w-4" />;
			default:
				return <Mail className="h-4 w-4" />;
		}
	}

	function formatDate(date?: string) {
		if (!date) return "-";
		return format(new Date(date), "dd MMM yyyy HH:mm", { locale: fr });
	}

	function nextPage() {
		if (notifications && currentPage < notifications.totalPages - 1) {
			setCurrentPage(currentPage + 1);
		}
	}

	function prevPage() {
		if (currentPage > 0) {
			setCurrentPage(currentPage - 1);
		}
	}

	const filteredNotifications =
		notifications?.content?.filter((n: Notification) => {
			if (filterStatus !== "ALL" && n.status !== filterStatus) return false;
			if (!searchTerm) return true;
			const search = searchTerm.toLowerCase();
			return (
				n.recipient?.toLowerCase().includes(search) ||
				n.subject?.toLowerCase().includes(search) ||
				n.content?.toLowerCase().includes(search)
			);
		}) || [];

	const handleRetry = (notificationId: string) => {
		retryMutation.mutate({
			path: {
				notificationId,
			},
		});
	};

	const handleRetryAll = () => {
		if (window.confirm(`Voulez-vous vraiment relancer ${failedNotifications?.length || 0} notifications échouées ?`)) {
			retryAllMutation.mutate();
		}
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-96">
				<Loader className="h-8 w-8 animate-spin text-blue-600" />
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="mb-6">
				<div className="flex justify-between items-center">
					<div>
						<h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
						<p className="text-gray-600 mt-2">
							Historique de toutes les notifications envoyées par le système
						</p>
					</div>
					{failedNotifications && failedNotifications.length > 0 && (
						<div className="flex items-center gap-4">
							<div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg">
								<AlertCircle className="h-5 w-5" />
								<span className="font-medium">{failedNotifications.length} notification(s) échouée(s)</span>
							</div>
							<Button
								onClick={handleRetryAll}
								disabled={retryAllMutation.isPending}
								variant="destructive"
							>
								{retryAllMutation.isPending ? (
									<>
										<Loader className="h-4 w-4 mr-2 animate-spin" />
										Relance en cours...
									</>
								) : (
									<>
										<RefreshCw className="h-4 w-4 mr-2" />
										Relancer toutes
									</>
								)}
							</Button>
						</div>
					)}
				</div>
			</div>

			{error && (
				<div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
					Erreur lors du chargement des notifications
				</div>
			)}

			<div className="bg-white rounded-lg shadow">
				<div className="p-4 border-b">
					<div className="flex justify-between items-center">
						<div className="flex items-center gap-4">
							<div className="relative">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
								<input
									type="text"
									placeholder="Rechercher..."
									className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
								/>
							</div>

							<div className="flex items-center gap-2">
								<label htmlFor="filterStatus" className="text-sm text-gray-600">
									Statut :
								</label>
								<select
									id="filterStatus"
									className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
									value={filterStatus}
									onChange={(e) => setFilterStatus(e.target.value)}
								>
									<option value="ALL">Tous</option>
									<option value="SENT">Envoyées</option>
									<option value="FAILED">Échouées</option>
									<option value="PENDING">En attente</option>
								</select>
							</div>
						</div>

						<div className="flex items-center gap-4">
							<div className="flex items-center gap-2">
								<label htmlFor="pageSize" className="text-sm text-gray-600">
									Afficher :
								</label>
								<select
									id="pageSize"
									className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
									value={pageSize}
									onChange={(e) => {
										setPageSize(Number(e.target.value));
										setCurrentPage(0);
									}}
								>
									<option value="20">20</option>
									<option value="50">50</option>
									<option value="100">100</option>
								</select>
							</div>
						</div>
					</div>
				</div>

				<div className="overflow-x-auto">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Type
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Destinataire
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Sujet
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Statut
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Date d'envoi
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Action
								</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{filteredNotifications.map((notification: Notification) => (
								<tr key={notification.id} className="hover:bg-gray-50">
									<td className="px-6 py-4 whitespace-nowrap">
										<div className="flex items-center gap-2">
											{getTypeIcon(notification.type)}
											<span className="text-sm text-gray-900">
												{notification.type}
											</span>
										</div>
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
										{notification.recipient || "-"}
									</td>
									<td className="px-6 py-4 whitespace-nowrap">
										<div className="max-w-xs truncate text-sm text-gray-900">
											{notification.subject || "-"}
										</div>
									</td>
									<td className="px-6 py-4 whitespace-nowrap">
										<span
											className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(notification.status)}`}
										>
											{notification.status}
										</span>
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
										{formatDate(notification.sentAt)}
									</td>
									<td className="px-6 py-4 whitespace-nowrap">
										<div className="flex items-center gap-2">
											<Button
												size="sm"
												variant="outline"
												onClick={() => {
													setSelectedNotification(notification);
													setDetailsDialogOpen(true);
												}}
											>
												<Eye className="h-3 w-3 mr-1" />
												Voir
											</Button>
											{notification.status === "FAILED" && (
												<Button
													size="sm"
													variant="outline"
													onClick={() => handleRetry(notification.id || "")}
													disabled={retryMutation.isPending}
												>
													{retryMutation.isPending ? (
														<Loader className="h-3 w-3 mr-1 animate-spin" />
													) : (
														<RefreshCw className="h-3 w-3 mr-1" />
													)}
													Relancer
												</Button>
											)}
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<div className="p-4 border-t">
					<div className="flex justify-between items-center">
						<div className="text-sm text-gray-600">
							Page {currentPage + 1} sur {notifications?.totalPages || 1}(
							{notifications?.totalElements || 0} notifications au total)
						</div>

						<div className="flex gap-2">
							<button
								onClick={prevPage}
								disabled={currentPage === 0}
								className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								<ChevronLeft className="h-4 w-4" />
								Précédent
							</button>
							<button
								onClick={nextPage}
								disabled={
									!notifications || currentPage >= notifications.totalPages - 1
								}
								className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Suivant
								<ChevronRight className="h-4 w-4" />
							</button>
						</div>
					</div>
				</div>
			</div>

			<Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
				<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Détails de la notification</DialogTitle>
						<DialogDescription>
							Informations complètes sur la notification
						</DialogDescription>
					</DialogHeader>
					{selectedNotification && (
						<div className="space-y-4">
							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="text-sm font-medium text-gray-700">
										Type
									</label>
									<div className="flex items-center gap-2 mt-1">
										{getTypeIcon(selectedNotification.type)}
										<span className="text-sm">{selectedNotification.type}</span>
									</div>
								</div>
								<div>
									<label className="text-sm font-medium text-gray-700">
										Statut
									</label>
									<div className="mt-1">
										<span
											className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedNotification.status)}`}
										>
											{selectedNotification.status}
										</span>
									</div>
								</div>
							</div>

							<div>
								<label className="text-sm font-medium text-gray-700">
									Destinataire
								</label>
								<p className="mt-1 text-sm text-gray-900">
									{selectedNotification.recipient || "-"}
								</p>
							</div>

							<div>
								<label className="text-sm font-medium text-gray-700">
									Sujet
								</label>
								<p className="mt-1 text-sm text-gray-900">
									{selectedNotification.subject || "-"}
								</p>
							</div>

							<div>
								<label className="text-sm font-medium text-gray-700">
									Date d'envoi
								</label>
								<p className="mt-1 text-sm text-gray-900">
									{formatDate(selectedNotification.sentAt)}
								</p>
							</div>

							{selectedNotification.status === "FAILED" &&
								selectedNotification.errorMessage && (
									<div className="p-4 bg-red-50 border border-red-200 rounded-lg">
										<label className="text-sm font-medium text-red-900">
											Message d'erreur
										</label>
										<p className="mt-1 text-sm text-red-700">
											{selectedNotification.errorMessage}
										</p>
									</div>
								)}

							<div>
								<label className="text-sm font-medium text-gray-700">
									Message
								</label>
								{selectedNotification.type === "EMAIL" ? (
									<div className="mt-2 border rounded-lg p-4 bg-gray-50">
										<div
											dangerouslySetInnerHTML={{
												__html: selectedNotification.content || "",
											}}
										/>
									</div>
								) : (
									<p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
										{selectedNotification.content || "-"}
									</p>
								)}
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
